import { useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/Layout";
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Building, 
  Mail, 
  Phone, 
  MapPin, 
  Briefcase,
  Eye,
  Zap,
  Layers,
  CheckCircle,
  Download,
  Shield,
  Clock,
  Users,
  Star
} from "lucide-react";
import React from "react";
import {userApi} from "@/api";

const Trial = () => {
  const navigate = useNavigate();

  // 检查localStorage中是否有token
  // todo 不管存不存在token,先跳转到注册页面，后期优化
  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/register');
    }else {
      navigate('/register');
    }
  }, [navigate]);

  const [userInfo, setUserInfo] = useState(null);

  // const getUserInfo = async () => {
  //   try {
  //     const response = await userApi.getUserDetail();
  //     setUserInfo(response?.data)
  //     localStorage.setItem('userInfo', JSON.stringify(response?.data));
  //   }catch (error) {
  //     console.error(error);
  //   }
  // }
  // useEffect(() => {
  //   getUserInfo()
  // }, []);

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // 个人信息
    name: "",
    email: "",
    phone: "",
    company: "",
    position: "",
    city: "",
    
    // 产品选择
    selectedProducts: [] as string[],
    
    // 验证码
    verificationCode: "",
    
    // 同意条款
    agreeTerms: false,
    agreeMarketing: false
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  const products = [
    {
      id: "seeod",
      name: "SeeOD",
      title: "几何光学仿真",
      description: "高精度光线追迹、多变量多目标优化和公差分析",
      icon: <Eye className="w-6 h-6" />,
      features: ["光线追迹", "系统优化", "公差分析", "成像模拟"],
      trialDays: 30,
      color: "bg-blue-50 border-blue-200 text-blue-700"
    },
    // {
    //   id: "seelight",
    //   name: "SeeLight",
    //   title: "物理光学仿真",
    //   description: "专业级激光/光纤仿真，72个基础光学元件模型",
    //   icon: <Zap className="w-6 h-6" />,
    //   features: ["激光仿真", "光纤建模", "52个实例", "教学适用"],
    //   trialDays: 30,
    //   color: "bg-green-50 border-green-200 text-green-700"
    // },
    {
      id: "seethinfilm",
      name: "SeeTF",
      title: "薄膜优化与误差分析",
      description: "光学薄膜系统优化设计，支持多种膜系设计方法",
      icon: <Layers className="w-6 h-6" />,
      features: ["薄膜设计", "误差分析", "光谱仿真", "蒙特卡洛"],
      trialDays: 30,
      color: "bg-purple-50 border-purple-200 text-purple-700"
    }
  ];

  const positions = [
    "研发工程师",
    "光学工程师",
    "系统工程师",
    "技术经理",
    "项目经理",
    "教授/副教授",
    "博士生/硕士生",
    "本科生",
    "其他"
  ];

  const handleInputChange = (field: string, value: string | boolean | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProductToggle = useCallback((productId: string) => {
    console.log(productId)
    setFormData(prev => ({
      ...prev,
      selectedProducts: prev.selectedProducts.includes(productId)
        ? prev.selectedProducts.filter(id => id !== productId)
        : [...prev.selectedProducts, productId]
    }));
  }, []);

  const sendVerificationCode = () => {
    if (!formData.phone) {
      alert("请先输入手机号码");
      return;
    }
    // 这里可以添加发送验证码的逻辑
    setVerificationSent(true);
    alert("验证码已发送到您的手机");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (currentStep === 1) {
      // 验证第一步信息
      if (!formData.name || !formData.email || !formData.phone || !formData.company) {
        alert("请填写完整的基本信息");
        return;
      }
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // 验证第二步信息
      if (formData.selectedProducts.length === 0) {
        alert("请至少选择一个产品");
        return;
      }
      setCurrentStep(3);
    } else if (currentStep === 3) {
      // 验证第三步信息
      if (!formData.verificationCode) {
        alert("请输入验证码");
        return;
      }
      if (!formData.agreeTerms) {
        alert("请同意服务条款");
        return;
      }
      // 提交表单
      setIsSubmitted(true);
    }
  };

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (isSubmitted) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <Card className="max-w-2xl mx-auto">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                申请提交成功！
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                感谢您申请试用See系列光学仿真软件。我们已收到您的申请，
                将在24小时内通过邮件向您发送试用版本和使用指南。
              </p>
              
              <div className="bg-blue-50 rounded-lg p-6 mb-6">
                <h3 className="font-semibold text-blue-900 mb-3">您申请的产品：</h3>
                <div className="flex flex-wrap gap-2 justify-center">
                  {formData.selectedProducts.map(productId => {
                    const product = products.find(p => p.id === productId);
                    return (
                      <Badge key={productId} className="bg-blue-100 text-blue-700">
                        {product?.name} - {product?.trialDays}天试用
                      </Badge>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-3 text-sm text-gray-600 mb-6">
                <div className="flex items-center justify-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>试用版本将发送至：{formData.email}</span>
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>技术支持联系：{formData.phone}</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={() => window.location.href = "/"}>
                  返回首页
                </Button>
                <Button variant="outline" onClick={() => window.location.href = "/manual"}>
                  查看使用手册
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            产品下载申请
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            免费试用See系列光学仿真软件，体验专业的光学仿真解决方案
          </p>
          
          {/* Progress Steps */}
          <div className="flex items-center justify-center space-x-4 mb-8">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step <= currentStep 
                    ? "bg-blue-600 text-white" 
                    : "bg-gray-200 text-gray-600"
                }`}>
                  {step < currentStep ? <CheckCircle className="w-4 h-4" /> : step}
                </div>
                {step < 3 && (
                  <div className={`w-12 h-0.5 mx-2 ${
                    step < currentStep ? "bg-blue-600" : "bg-gray-200"
                  }`} />
                )}
              </div>
            ))}
          </div>
          
          <div className="text-sm text-gray-600">
            步骤 {currentStep} / 3: {
              currentStep === 1 ? "基本信息" :
              currentStep === 2 ? "产品选择" : "验证确认"
            }
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">
                {currentStep === 1 && "填写基本信息"}
                {currentStep === 2 && "选择试用产品"}
                {currentStep === 3 && "验证并确认"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit}>
                {/* Step 1: Basic Information */}
                {currentStep === 1 && (
                  <div className="space-y-6 mb-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="flex items-center space-x-2 mb-2">
                          <User className="w-4 h-4" />
                          <span>姓名 *</span>
                        </Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => handleInputChange("name", e.target.value)}
                          placeholder="请输入您的姓名"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="flex items-center space-x-2 mb-2">
                          <Mail className="w-4 h-4" />
                          <span>邮箱 *</span>
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          placeholder="请输入邮箱地址"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone" className="flex items-center space-x-2 mb-2">
                          <Phone className="w-4 h-4" />
                          <span>手机号码 *</span>
                        </Label>
                        <Input
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => handleInputChange("phone", e.target.value)}
                          placeholder="请输入手机号码"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="company" className="flex items-center space-x-2 mb-2">
                          <Building className="w-4 h-4" />
                          <span>公司/学校 *</span>
                        </Label>
                        <Input
                          id="company"
                          value={formData.company}
                          onChange={(e) => handleInputChange("company", e.target.value)}
                          placeholder="请输入公司或学校名称"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="position" className="flex items-center space-x-2 mb-2">
                          <Briefcase className="w-4 h-4" />
                          <span>职业</span>
                        </Label>
                        <Select value={formData.position} onValueChange={(value) => handleInputChange("position", value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="请选择您的职业" />
                          </SelectTrigger>
                          <SelectContent>
                            {positions.map((position) => (
                              <SelectItem key={position} value={position}>
                                {position}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="city" className="flex items-center space-x-2 mb-2">
                          <MapPin className="w-4 h-4" />
                          <span>城市</span>
                        </Label>
                        <Input
                          id="city"
                          value={formData.city}
                          onChange={(e) => handleInputChange("city", e.target.value)}
                          placeholder="请输入所在城市"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Product Selection */}
                {currentStep === 2 && (
                  <div className="space-y-6">
                    <div className="text-center mb-8">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        选择您要试用的产品
                      </h3>
                      <p className="text-gray-600">
                        您可以选择一个或多个产品进行试用，每个产品提供30天免费试用期
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {products.map((product) => (
                        <Card 
                          key={product.id} 
                          className={`cursor-pointer transition-all duration-200 ${
                            formData.selectedProducts.includes(product.id)
                              ? `${product.color} border-2`
                              : "hover:shadow-lg border-2 border-transparent"
                          }`}
                          // onClick={() => handleProductToggle(product.id)}
                        >
                          <CardContent className="p-6"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation(); // 防止事件冒泡
                              handleProductToggle(product.id);
                            }}
                          >
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center space-x-3">
                                <div className={`p-2 rounded-lg ${
                                  formData.selectedProducts.includes(product.id)
                                    ? "bg-white/50"
                                    : "bg-gray-100"
                                }`}>
                                  {product.icon}
                                </div>
                                <div>
                                  <h4 className="font-bold text-lg">{product.name}</h4>
                                  <p className="text-sm opacity-75">{product.title}</p>
                                </div>
                              </div>
                              <div onClick={(e) => e.stopPropagation()}>
                                <Checkbox 
                                  checked={formData.selectedProducts.includes(product.id)}
                                  onChange={() => handleProductToggle(product.id)}
                                />
                              </div>
                              {/* <Checkbox 
                                checked={formData.selectedProducts.includes(product.id)}
                                onChange={() => handleProductToggle(product.id)}
                              /> */}
                            </div>

                            <p className="text-sm mb-4 opacity-90">
                              {product.description}
                            </p>

                            <div className="space-y-2 mb-4">
                              {product.features.map((feature, idx) => (
                                <div key={idx} className="flex items-center text-sm">
                                  <CheckCircle className="w-3 h-3 mr-2 opacity-60" />
                                  {feature}
                                </div>
                              ))}
                            </div>

                            <div className="flex items-center justify-between text-sm">
                              <Badge variant="outline" className="bg-white/50">
                                <Clock className="w-3 h-3 mr-1" />
                                {product.trialDays}天试用
                              </Badge>
                              <span className="font-medium">免费</span>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {formData.selectedProducts.length > 0 && (
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h4 className="font-medium text-blue-900 mb-2">
                          已选择 {formData.selectedProducts.length} 个产品
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {formData.selectedProducts.map(productId => {
                            const product = products.find(p => p.id === productId);
                            return (
                              <Badge key={productId} className="bg-blue-100 text-blue-700">
                                {product?.name}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 3: Verification */}
                {currentStep === 3 && (
                  <div className="space-y-6">
                    <div className="text-center mb-8">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        手机验证
                      </h3>
                      <p className="text-gray-600">
                        为了确保信息安全，请验证您的手机号码
                      </p>
                    </div>

                    <div className="max-w-md mx-auto">
                      <div className="flex space-x-2 mb-4">
                        <Input
                          value={formData.verificationCode}
                          onChange={(e) => handleInputChange("verificationCode", e.target.value)}
                          placeholder="请输入验证码"
                          className="flex-1"
                        />
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={sendVerificationCode}
                          disabled={verificationSent}
                        >
                          {verificationSent ? "已发送" : "发送验证码"}
                        </Button>
                      </div>
                      
                      <p className="text-sm text-gray-500 text-center mb-6">
                        验证码将发送到 {formData.phone}
                      </p>
                    </div>

                    {/* Terms and Conditions */}
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <Checkbox 
                          id="terms"
                          checked={formData.agreeTerms}
                          onCheckedChange={(checked) => handleInputChange("agreeTerms", checked)}
                        />
                        <Label htmlFor="terms" className="text-sm leading-relaxed">
                          我已阅读并同意 
                          <a href="#" className="text-blue-600 hover:underline">《服务条款》</a> 
                          和 
                          <a href="#" className="text-blue-600 hover:underline">《隐私政策》</a>
                        </Label>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <Checkbox 
                          id="marketing"
                          checked={formData.agreeMarketing}
                          onCheckedChange={(checked) => handleInputChange("agreeMarketing", checked)}
                        />
                        <Label htmlFor="marketing" className="text-sm leading-relaxed">
                          我同意接收光智云的产品更新和营销信息（可选）
                        </Label>
                      </div>
                    </div>

                    {/* Summary */}
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h4 className="font-medium text-gray-900 mb-4">申请摘要</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">申请人：</span>
                          <span className="font-medium">{formData.name}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">公司：</span>
                          <span className="font-medium">{formData.company}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">邮箱：</span>
                          <span className="font-medium">{formData.email}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">手机：</span>
                          <span className="font-medium">{formData.phone}</span>
                        </div>
                      </div>
                      <div className="mt-4">
                        <span className="text-gray-600">试用产品：</span>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {formData.selectedProducts.map(productId => {
                            const product = products.find(p => p.id === productId);
                            return (
                              <Badge key={productId} variant="outline">
                                {product?.name}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between pt-6 border-t">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={goBack}
                    disabled={currentStep === 1}
                  >
                    上一步
                  </Button>
                  <Button type="submit">
                    {currentStep === 3 ? "提交申请" : "下一步"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              为什么选择我们的试用服务？
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Download className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">完全免费</h3>
              <p className="text-gray-600 text-sm">30天完整功能试用，无任何费用</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">专业支持</h3>
              <p className="text-gray-600 text-sm">专业技术团队提供全程指导</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">数据安全</h3>
              <p className="text-gray-600 text-sm">严格保护您的数据和隐私安全</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-6 h-6 text-yellow-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">完整体验</h3>
              <p className="text-gray-600 text-sm">体验所有功能，无任何限制</p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Trial;